/**
 * 几何精灵离屏缓存：所有外形仍是程序化几何（无位图素材），
 * 首次使用时绘制到 2× 超采样的离屏 canvas，运行时 drawImage。
 */
export interface Sprite {
  canvas: HTMLCanvasElement;
  /** 绘制中心到边的 CSS 像素（图像实际为 2× 超采样）。 */
  half: number;
}

const cache = new Map<string, Sprite>();
const SS = 2; // 超采样倍数，覆盖 dpr ≤ 2

function make(half: number, draw: (ctx: CanvasRenderingContext2D, s: number) => void): Sprite {
  const css = Math.ceil(half);
  const size = css * 2 * SS;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('offscreen 2d unavailable');
  ctx.translate(size / 2, size / 2);
  ctx.scale(SS, SS);
  draw(ctx, 1);
  return { canvas, half: css };
}

/** 取 / 建精灵。half 为含描边余量的半径（CSS px）。 */
export function sprite(
  key: string,
  half: number,
  draw: (ctx: CanvasRenderingContext2D, s: number) => void,
): Sprite {
  let sp = cache.get(key);
  if (!sp) {
    sp = make(half, draw);
    cache.set(key, sp);
  }
  return sp;
}

/** 以 (x,y) 为中心按 scale 绘制（无旋转）。 */
export function drawSprite(
  ctx: CanvasRenderingContext2D, sp: Sprite, x: number, y: number, scale = 1,
): void {
  const h = sp.half * scale;
  ctx.drawImage(sp.canvas, x - h, y - h, h * 2, h * 2);
}

/** 以 (x,y) 为中心，旋转 angle 后按 scale 绘制。 */
export function drawSpriteRot(
  ctx: CanvasRenderingContext2D,
  sp: Sprite, x: number, y: number, angle: number, scale = 1,
): void {
  const h = sp.half * scale;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.drawImage(sp.canvas, -h, -h, h * 2, h * 2);
  ctx.restore();
}

/**
 * 高性能版：调用方提供世界基准变换 base，绘制后 setTransform 复位，
 * 避免每个旋转精灵一次 save/restore。
 */
export function drawSpriteRotT(
  ctx: CanvasRenderingContext2D, sp: Sprite,
  x: number, y: number, angle: number, scale: number, base: DOMMatrix,
): void {
  const h2 = sp.half * scale;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.drawImage(sp.canvas, -h2, -h2, h2 * 2, h2 * 2);
  ctx.setTransform(base);
}
